import { useEpdLcaCustomResultGridData } from 'services/api/queries';

import {
  createVariationColumnWithDisclaimer,
  LcaResultsGridColumns,
  LcaResultsGridColumnsPdf,
} from '../LcaResultsGridColumns';
import LcaCustomFixedResultsGrid from './LcaCustomFixedResultsGrid';
import LcaResultsGridPdf from '../LcaResultsGridPdf';
import { LcaGridNote } from '../../GridService';
import useLcaGridHelper from 'components/v2/compiler/hooks/useLcaGridHelper';

type TProps = {
  epdVersionId: string;
  customResultId: string;
  isForPdf?: boolean;
  disabled?: boolean;
  preferEnabledAppearanceEvenWhenDisabled?: boolean;
  className?: string;
};

const LcaCustomBestWorstResultsGrid = ({
  epdVersionId,
  customResultId,
  isForPdf,
  disabled,
  preferEnabledAppearanceEvenWhenDisabled,
  className,
}: TProps) => {
  const rows = useEpdLcaCustomResultGridData(customResultId).data;

  const { generalDisclaimerNote, showVariationColumn, createVariationDisclaimerNote } = useLcaGridHelper(epdVersionId);

  const notes: LcaGridNote[] = [
    {
      note: 'Acronyms',
      description: 'GWP-GHG = Global warming potential greenhouse gas.',
    },
  ];

  if (generalDisclaimerNote) {
    notes.push(generalDisclaimerNote);
  }

  notes.push({
    note: 'Disclaimer 1',
    description: (
      <>
        The GWP-GHG indicator is termed GWP-IOBC/GHG in the ILCD+EPD+ data format. The indicator accounts for all greenhouse
        gases except biogenic carbon dioxide uptake and emissions and biogenic carbon stored in the product. As such, the
        indicator is identical to GWP-total except that the CF for biogenic CO<sub>2</sub> is set to zero.
      </>
    ),
  });

  let columns = isForPdf ? LcaResultsGridColumnsPdf : LcaResultsGridColumns;
  if (showVariationColumn && (!isForPdf || (isForPdf && rows?.some((r) => r.variation)))) {
    const disclaimerNumber = 2;
    columns = [...columns, createVariationColumnWithDisclaimer(disclaimerNumber)];
    notes.push(createVariationDisclaimerNote(disclaimerNumber));
  }

  if (isForPdf) {
    return <LcaResultsGridPdf rows={rows} columns={columns} notes={notes} />;
  }

  return (
    <LcaCustomFixedResultsGrid
      className={className}
      epdVersionId={epdVersionId}
      customResultId={customResultId}
      title="Additional mandatory environmental performance indicators"
      rows={rows}
      columns={columns}
      notes={notes}
      disabled={disabled}
      preferEnabledAppearanceEvenWhenDisabled={preferEnabledAppearanceEvenWhenDisabled}
    />
  );
};

export default LcaCustomBestWorstResultsGrid;
